// Предопределенные конфигурации гайдов
const GUIDE_CONFIGURATIONS = {
  'clients-guide': {
    guideId: 'clients-guide',
    totalSteps: 6,
    title: 'Гайд по клиентам',
    description: 'Изучите основы работы с клиентами',
  },
  'orders-guide': {
    guideId: 'orders-guide',
    totalSteps: 5,
    title: 'Гайд по заказам',
    description: 'Научитесь управлять заказами',
  },
  'products-guide': {
    guideId: 'products-guide',
    totalSteps: 4,
    title: 'Гайд по товарам',
    description: 'Управление каталогом товаров',
  },
};

const state = {
  // Предопределенные конфигурации гайдов
  guideConfigurations: GUIDE_CONFIGURATIONS,
  // Текущие активные гайды (состояния пользователя)
  activeGuides: {},
  // Загружается ли состояние гайда
  loading: false,
  // Ошибки
  error: null,
  // Показывать ли гайд в данный момент
  isGuideVisible: false,
  // ID текущего активного гайда
  currentGuideId: null,
};

const mutations = {
  SET_GUIDE_STATE(state, { guideId, guideState }) {
    state.activeGuides = {
      ...state.activeGuides,
      [guideId]: guideState,
    };
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  SET_GUIDE_VISIBILITY(state, isVisible) {
    state.isGuideVisible = isVisible;
  },

  SET_CURRENT_GUIDE_ID(state, guideId) {
    state.currentGuideId = guideId;
  },

  CLEAR_GUIDE_STATE(state, guideId) {
    const newActiveGuides = { ...state.activeGuides };
    delete newActiveGuides[guideId];
    state.activeGuides = newActiveGuides;
  },

  CLEAR_ALL_GUIDES(state) {
    state.activeGuides = {};
    state.currentGuideId = null;
    state.isGuideVisible = false;
  },
};

const actions = {
  /**
   * Загрузить состояние гайда
   */
  loadGuideState({ commit, state }, guideId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      // Проверяем, есть ли уже состояние для этого гайда
      let guideState = state.activeGuides[guideId];

      if (!guideState) {
        // Если состояния нет, создаем новое на основе конфигурации
        const config = state.guideConfigurations[guideId];
        if (!config) {
          throw new Error(`Конфигурация для гайда ${guideId} не найдена`);
        }

        guideState = {
          guideId,
          isCompleted: false,
          currentStep: 0,
          totalSteps: config.totalSteps,
          lastUpdated: new Date().toISOString(),
        };

        commit('SET_GUIDE_STATE', { guideId, guideState });
      }

      return guideState;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Обновить состояние гайда
   */
  updateGuideState({ commit, state }, { guideId, stateData }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const currentState = state.activeGuides[guideId];
      if (!currentState) {
        throw new Error(`Гайд ${guideId} не найден`);
      }

      const updatedState = {
        ...currentState,
        ...stateData,
        lastUpdated: new Date().toISOString(),
      };

      commit('SET_GUIDE_STATE', { guideId, guideState: updatedState });
      return updatedState;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Завершить шаг гайда
   */
  completeStep({ commit, state, dispatch }, { guideId, stepNumber }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const currentState = state.activeGuides[guideId];
      if (!currentState) {
        throw new Error(`Гайд ${guideId} не найден`);
      }

      // stepNumber уже содержит правильный индекс следующего шага (0-based)
      const nextStep = stepNumber;
      const isCompleted = nextStep >= currentState.totalSteps;

      const updatedState = {
        ...currentState,
        currentStep: nextStep,
        isCompleted,
        lastUpdated: new Date().toISOString(),
      };

      commit('SET_GUIDE_STATE', { guideId, guideState: updatedState });

      // Если гайд завершен, скрыть его
      if (isCompleted) {
        commit('SET_GUIDE_VISIBILITY', false);
      }

      return updatedState;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Сбросить гайд
   */
  resetGuide({ commit, state }, guideId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const config = state.guideConfigurations[guideId];
      if (!config) {
        throw new Error(`Конфигурация для гайда ${guideId} не найдена`);
      }

      const resetState = {
        guideId,
        isCompleted: false,
        currentStep: 0,
        totalSteps: config.totalSteps,
        lastUpdated: new Date().toISOString(),
      };

      commit('SET_GUIDE_STATE', { guideId, guideState: resetState });
      return resetState;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Инициализировать новый гайд
   */
  initializeGuide({ commit, state }, { guideId, totalSteps }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      // Используем totalSteps из конфигурации, если не передан явно
      const config = state.guideConfigurations[guideId];
      const finalTotalSteps = totalSteps || (config ? config.totalSteps : 0);

      if (finalTotalSteps === 0) {
        throw new Error(`Не удалось определить количество шагов для гайда ${guideId}`);
      }

      const initialState = {
        guideId,
        isCompleted: false,
        currentStep: 0,
        totalSteps: finalTotalSteps,
        lastUpdated: new Date().toISOString(),
      };

      commit('SET_GUIDE_STATE', { guideId, guideState: initialState });
      return initialState;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Показать гайд
   */
  showGuide({ commit }, guideId) {
    commit('SET_CURRENT_GUIDE_ID', guideId);
    commit('SET_GUIDE_VISIBILITY', true);
  },

  /**
   * Скрыть гайд
   */
  hideGuide({ commit }) {
    commit('SET_GUIDE_VISIBILITY', false);
    commit('SET_CURRENT_GUIDE_ID', null);
  },

  /**
   * Загрузить все гайды пользователя
   */
  loadAllGuides({ commit, state }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      // Возвращаем все активные гайды из store
      const guides = Object.values(state.activeGuides);
      return guides;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

const getters = {
  /**
   * Получить конфигурацию гайда
   */
  getGuideConfiguration: (state) => (guideId) => {
    return state.guideConfigurations[guideId] || null;
  },

  /**
   * Получить все доступные конфигурации гайдов
   */
  getAllGuideConfigurations: (state) => {
    return state.guideConfigurations;
  },

  /**
   * Получить состояние конкретного гайда
   */
  getGuideById: (state) => (guideId) => {
    return state.activeGuides[guideId] || null;
  },

  /**
   * Получить состояние конкретного гайда (алиас для getGuideById)
   */
  getGuideState: (state) => (guideId) => {
    return state.activeGuides[guideId] || null;
  },

  /**
   * Проверить, завершен ли гайд
   */
  isGuideCompleted: (state) => (guideId) => {
    const guide = state.activeGuides[guideId];
    return guide ? guide.isCompleted : false;
  },

  /**
   * Получить текущий шаг гайда
   */
  getCurrentStep: (state) => (guideId) => {
    const guide = state.activeGuides[guideId];
    return guide ? guide.currentStep : 0;
  },

  /**
   * Получить прогресс гайда в процентах
   */
  getGuideProgress: (state) => (guideId) => {
    const guide = state.activeGuides[guideId];
    if (!guide || guide.totalSteps === 0) return 0;

    // Если гайд завершен, возвращаем 100%
    if (guide.isCompleted) return 100;

    // Прогресс = количество завершенных шагов / общее количество шагов
    // currentStep показывает индекс текущего активного шага (0-based)
    // Поэтому завершенных шагов = currentStep
    return Math.round((guide.currentStep / guide.totalSteps) * 100);
  },

  /**
   * Получить общее количество шагов в гайде
   */
  getTotalStep: (state) => (guideId) => {
    const guide = state.activeGuides[guideId];
    if (guide) return guide.totalSteps;

    // Если состояния нет, берем из конфигурации
    const config = state.guideConfigurations[guideId];
    return config ? config.totalSteps : 0;
  },

  /**
   * Получить все активные гайды
   */
  getAllActiveGuides: (state) => {
    return state.activeGuides;
  },

  /**
   * Проверить, показывается ли гайд
   */
  isGuideVisible: (state) => {
    return state.isGuideVisible;
  },

  /**
   * Получить ID текущего активного гайда
   */
  getCurrentGuideId: (state) => {
    return state.currentGuideId;
  },

  /**
   * Получить текущий активный гайд
   */
  getCurrentGuide: (state) => {
    if (!state.currentGuideId) return null;
    return state.activeGuides[state.currentGuideId] || null;
  },

  /**
   * Проверить, загружается ли состояние
   */
  isLoading: (state) => {
    return state.loading;
  },

  /**
   * Получить ошибку
   */
  getError: (state) => {
    return state.error;
  },

  /**
   * Проверить, инициализирован ли гайд
   */
  isGuideInitialized: (state) => (guideId) => {
    return !!state.activeGuides[guideId];
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
